import React, { useEffect, useState } from "react";
import { Grid, Box, Button, Typography, MenuItem, Select, FormControl, InputLabel } from "@mui/material";
import StackedBarChart from "../StackedBarChart/StackedBarChart";
import { BarChart } from "@mui/x-charts";
import CustomPieChart from "../DonutChart/DonutChart";
import StpTrendsLineChart from "../LineChart/StpTrendsLineChart";
import AgingTable from "../AgingTable/AgingTable";
import TrendLineChartForBu from "../LineChart/TrendLineChartForBu";
import { useGetBusinessUnitGlobalFilter } from "../../hooks/Reporting";
import { CircularProgress } from "@mui/material";

const complexityTypes = [
    // { value: "", label: "Select complexity type" },
    { value: "Single_Po", label: "Single PO" },
    { value: "Multi_Po", label: "Multi PO" },
    { value: "Non_Po", label: "Non PO" },
    { value: "Yet_To_Be_Extracted", label: "Yet To Be Extracted" },
];

const invoiceStatuses = [
    { value: "Processed", label: "Processed" },
    { value: "Pending", label: "Pending" },
    { value: "Rejected", label: "Rejected" },
];
const rejectionTypes =  [
  "automatic", "manual"
];


export default function BusinessUnitReport({ data, onBuReportFilterApply, onClear, initialFilters, isPending,invoiceStatusListData,invoiceRejectionListData}) {
    const [businessUnitRptFilters, setBusinessUnitRptFilters] = useState({
        invoice_status: initialFilters.invoice_status || [],
        complexity_type: initialFilters.complexity_type || [],
        rejection_type: initialFilters.rejection_type || []
    })
    const [invoicesByBu, setInvoicesByBu] = useState([])
    const [invoicesTopBuByVol, setInvoicesTopBuByVol] = useState([])
    const [invoicesBuByRejectionRate, setInvoicesBuByRejectionRate] = useState([])
    const [invoicesStatusPerBu, setInvoicesStatusPerBu] = useState([])
    const [buInvComplexity, setBuInvComplexity] = useState([])
    const [trendsOfInvByBu, setTrendsOfInvByBu] = useState([])
    const [buExceptionList, setBuExceptionList] = useState([])
    const { data: buFilterData } = useGetBusinessUnitGlobalFilter()

    useEffect(() => {
        setBusinessUnitRptFilters({
            invoice_status: initialFilters?.invoice_status || [],
            complexity_type: initialFilters?.complexity_type || [],
            rejection_type: initialFilters?.rejection_type || []
        })
    }, [initialFilters])

    const handleChange = (event) => {
        setBusinessUnitRptFilters((prev) => {
            return {
                ...prev,
                [event.target.name]: event.target.value
            }
        })
    }
    const handleApply = () => {
        onBuReportFilterApply(businessUnitRptFilters)
    }

    const handleReset = () => {
        setBusinessUnitRptFilters({
            invoice_status: [],
            complexity_type: [],
            rejection_type: []

        })
        onClear && onClear(3);
    }

    useEffect(() => {
        if (data?.total_by_bu?.length) {
            setInvoicesByBu(data?.total_by_bu)
        }else{
            setInvoicesByBu([])
        }
    }, [data?.total_by_bu])

    useEffect(() => {
        if (data?.top_bu_by_volume?.length) {
            setInvoicesTopBuByVol(data?.top_bu_by_volume)
        }else{
            setInvoicesTopBuByVol([])
        }
    }, [data?.top_bu_by_volume])
    useEffect(() => {
        if (data?.exceptions?.length) {
            setBuExceptionList(data?.exceptions)
        }else{
            setBuExceptionList([])
        }
    }, [data?.exceptions])

    useEffect(() => {
        if (data?.bu_complexity_pie?.length) {
            const buComplexPieChartData = data?.bu_complexity_pie.map((item, index) => ({
                id: index,
                value: item.count, // using the 'count' as 'value'
                label: item.complexity.charAt(0).toUpperCase()+item.complexity.slice(1).replace(/_/g, ' ').replace(/\bpo\b/g, "PO") // transforming 'invoicestatus' to 'label' with spaces
            }));
            setBuInvComplexity(buComplexPieChartData)
        }else{
            setBuInvComplexity([])
        }
    }, [data?.bu_complexity_pie])

    useEffect(() => {
        if(data?.invoices_by_status_per_bu?.length){
            setInvoicesStatusPerBu(data?.invoices_by_status_per_bu)
        }else{
            setInvoicesStatusPerBu([])
        }
    }, [data?.invoices_by_status_per_bu])

    useEffect(() => {
        if(data?.trend_by_bu?.length){
            setTrendsOfInvByBu(data?.trend_by_bu)
        }else{
            setTrendsOfInvByBu([])
        }
    }, [data?.trend_by_bu])

    useEffect(() => {
        if (data?.top_bu_by_rejection_rate?.length) {
            const transformedData = data.top_bu_by_rejection_rate.map((item, idx) => ({
                business_unit: item.business_unit,
                auto_rejections_pct: item.auto_rejections ,
                manual_rejections_pct: item.manual_rejections ,
            }));

            setInvoicesBuByRejectionRate(transformedData)
        }else{
            setInvoicesBuByRejectionRate([])
        }
    }, [data?.top_bu_by_rejection_rate])

    const series = [
        {
            dataKey: "count",
            label: "Invoices",
            color: "#3C2676"
        }
    ];

    const buRejRateSeries = [
        {
            dataKey: "auto_rejections_pct",
            label: "Automatic rejection",
            color: "#E2DCF2", // dark purple
            stack: 'rejection', // stack both
        },
        {
            dataKey: "manual_rejections_pct",
            label: "Manual rejection",
            color: "#543EA3", // lighter purple (adjust as needed)
            stack: 'rejection',
        }
    ];

    const invStatusPerBuSeries = [
        {
            dataKey: "rejected",
            label: "Rejected",
            color: "#3A238C",
            stack: 'status',
        },
        {
            dataKey: "processing",
            label: "Processed",
            color: "#543EA3",
            stack: 'status',
        },
        {
            dataKey: "pending",
            label: "Pending",
            color: "#6754AD",
            stack: 'status',
        },
    ];
    const coloursByStatusDonut = ['#A296CC', '#9384C7',
        '#7B6AB8', '#6754AD'
    ];
    const capitalizeWords = (value: string) => {
        if (!value) return "";

        const acronyms = ["PO", "BU"]; // Add more acronyms here as needed

        return value
            .replace(/_/g, " ")
            .split(" ")
            .map((word) => {
                const upperWord = word.toUpperCase();
                if (acronyms.includes(upperWord)) {
                    return upperWord;
                }
                return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
            })
            .join(" ");
    };

    const columns = [
        {
            field: 'invoiceid',
            headerName: 'Invoice ID',
            flex: 1,
            align: 'left',
            headerAlign: 'left',
        },
        {
            field: 'supplier',
            headerName: 'Supplier name',
            flex: 1,
            align: 'left',
            headerAlign: 'left',
            valueFormatter: ({ value }) => capitalizeWords(value)
        },
        {
            field: 'status',
            headerName: 'Status',
            flex: 1,
            align: 'left',
            headerAlign: 'left',
            valueFormatter: ({ value }) => capitalizeWords(value)
        },
        {
            field: 'complexity',
            headerName: 'Complexity',
            flex: 1,
            align: 'left',
            headerAlign: 'left',
            valueFormatter: ({ value }) => capitalizeWords(value)
        },
        {
            field: 'business_unit',
            headerName: 'Business unit',
            flex: 1,
            align: 'left',
            headerAlign: 'left',
            valueFormatter: ({ value }) => capitalizeWords(value)
        },
        {
            field: 'age',
            headerName: 'Age (in days)',
            type: 'number',
            flex: 1,
            align: 'left',
            headerAlign: 'left',
            valueFormatter: ({ value }) => (value !== "None" ? value : 0)
        },
    ] as const;

    return (
        <div style={{ backgroundColor: 'var(--background-off-white)' }}>
             {isPending ? <Box sx={{display: 'flex', justifyContent: 'center'}}><CircularProgress sx={{margin: '0 auto', mt: "2%"}}/></Box> :
            <div style={{ backgroundColor: 'var(--background-off-white)' }}>
                <Box sx={{ width: "100%", background: 'var(--background-off-white)', borderRadius: "8px", mb: 4 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'flex-end', // vertical alignment
                            gap: 3,
                            width: '100%',
                            background: 'var(--background-off-white)',
                            borderRadius: '8px',
                            mb: 2,
                        }}
                    >
                        {/* Invoice Status */}
                        <Box sx={{ display: 'flex', gap: "8px", justifyContent: 'center', alignItems: 'start', flexDirection: "column" ,width:'20%'}}>
                            <InputLabel sx={{ fontSize: '12px', fontWeight: '400' }}>
                                Invoice status
                            </InputLabel>
                            <FormControl fullWidth size="small">
                                <Select
                                    multiple
                                    value={businessUnitRptFilters.invoice_status}
                                    onChange={handleChange}
                                    name="invoice_status"
                                    displayEmpty
                                    renderValue={selected =>
                                        selected.length
                                            ? selected.map(opt => opt.replaceAll("_"," ")).join(",")
                                            : <span style={{ color: 'var(--brand-text-default)',fontSize:'14px' }}>Select invoice status</span>
                                    }
                                    sx={{ background: 'var(--white)', height: '38px', borderRadius: '6'  }}
                                >
                                    {invoiceStatusListData.map(value => (
                                        <MenuItem value={value} key={value}>
                                            {value.replaceAll("_"," ").replace(/\bErp\b/g, "ERP")}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>

                        {/* Complexity Type */}
                        <Box sx={{ display: 'flex', gap: "8px", justifyContent: 'center', alignItems: 'start', flexDirection: "column",width:'20%' }}>
                            <InputLabel sx={{ fontSize: '12px', fontWeight: '400' }}>
                                Complexity type
                            </InputLabel>
                            <FormControl fullWidth size="small">
                                <Select
                                    multiple
                                    value={businessUnitRptFilters.complexity_type}
                                    onChange={handleChange}
                                    name="complexity_type"
                                    displayEmpty
                                    renderValue={selected =>
                                        selected.length
                                            ? selected.map(opt => opt.replaceAll("_"," ")).join(",")
                                            : <span style={{ color: 'var(--brand-text-default)',fontSize:'14px'}}>Select complexity type</span>
                                    }
                                    sx={{ background: 'var(--white)', height: '38px', borderRadius: '6'  }}
                                >
                                    {complexityTypes.map(type => (
                                        <MenuItem value={type.value} key={type.value}>
                                            {type.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>

                        {/* Rejection Type */}
                        <Box sx={{ display: 'flex', gap: "8px", justifyContent: 'center', alignItems: 'start', flexDirection: "column" ,width:'20%'}}>
                            <InputLabel sx={{ fontSize: '12px', fontWeight: '400' }}>
                                Rejection type (automatic/manual)
                            </InputLabel>
                            <FormControl fullWidth size="small">
                                <Select
                                    multiple
                                    value={businessUnitRptFilters.rejection_type}
                                    onChange={handleChange}
                                    name="rejection_type"
                                    displayEmpty
                                    renderValue={selected =>
                                        selected.length
                                            ? selected.map(opt => opt.charAt(0).toUpperCase()+opt.slice(1)).join(",")
                                            : <span style={{ color: 'var(--brand-text-default)',fontSize:'14px'}}>Select rejection type</span>
                                    }
                                    sx={{ background: 'var(--white)', height: '38px', borderRadius: '6'  }}
                                >
                                    {rejectionTypes.map(value => (
                                        <MenuItem value={value} key={value}>
                                            {value.charAt(0).toUpperCase()+value.slice(1)}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>

                        {/* Actions */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, ml: 2, mb: '2px' }}>
                            <Button
                                variant="contained"
                                color="primary"
                                sx={{minWidth: '102px', fontSize: '14px', padding: '10px 12px', fontWeight: 600, lineHeight: '18px', height: 'auto'}}
                                onClick={handleApply}
                            >
                                Apply filters
                            </Button>
                            <Button variant="outlined" color="secondary" onClick={handleReset}  sx={{ fontSize: '14px', fontWeight: 600, }}>
                                Reset
                            </Button>
                        </Box>
                    </Box>
                </Box>
                <div style={{ display: 'flex', gap: '32px', flexWrap: 'wrap' }}>
                
                        <StackedBarChart
                            data={invoicesByBu}
                            series={series}
                            xAxisKey="business_unit"
                            xAxisLabel="Business unit"
                            yAxisLabel="Invoices"
                            title="Invoices Flagged for Review by BU"
                        />
                    

                        <StackedBarChart
                            data={invoicesTopBuByVol}
                            series={series}
                            xAxisKey="business_unit"
                            xAxisLabel="Business unit"
                            yAxisLabel="Invoices"
                            title="Top BUs by volume"
                        />

                        <Box sx={{
                            width: "100%",
                            boxSizing: "border-box",
                            padding: "24px",
                            background: "#fff",
                            border: "1px solid #E5E5E5",
                            borderRadius: "8px",
                        }}>
                        <Typography variant="h6" component="h4" sx={{ fontSize: '20px', fontWeight: 700, color: '#111', mb: 2 }}>
                            Top BUs by rejection rate
                        </Typography>
                        {
                            invoicesBuByRejectionRate?.length > 0 ? (
                        <BarChart
                            series={buRejRateSeries}
                            xAxis={[
                                {
                                    scaleType: "band",
                                    dataKey: "business_unit",
                                    label: "Business unit",
                                    valueFormatter: (value, context) => {
                                        if (context.location === "tick") {
                                            return value.length > 0 ? " " : value;
                                        }
                                        return value.charAt(0).toUpperCase()+value.slice(1)
                                    }
                                },
                            ]}
                            yAxis={[
                                {
                                    label: "Invoices",
                                    min: 0,
                                },
                            ]}
                            dataset={invoicesBuByRejectionRate}
                            height={280}
                        
                            sx={{
                                '& .MuiChartsAxis-root .MuiChartsAxis-line': { stroke: 'var(--grey-CCC)' },
                                '& .MuiChartsAxis-root .MuiChartsAxis-tick': { stroke: 'var(--grey-CCC)' },
                                '& .MuiChartsLegend-root': {
                                    flexDirection: 'row !important',
                                    marginBottom: '16px',
                                },
                                width:'100%'
                            }}
                            slotProps={{
                                legend: {
                                    sx: {
                                        width: '100%',
                                        justifyContent: 'center',
                                        marginBottom: '16px',
                                        flexDirection: 'row',
                                        textTransform:'capitalize',
                                        gap: '32px'
                                    },
                                },
                            }}
                        />
                        ):(<div style={{display:'flex',justifyContent:'center'}}>No data to display</div>)
                        }
                    </Box>
                    
                    <StackedBarChart
                        data={invoicesStatusPerBu}
                        series={invStatusPerBuSeries}
                        xAxisKey="business_unit"
                        xAxisLabel="Business unit"
                        yAxisLabel="Invoices"
                        title="Invoice status per BU"
                    />
                    <Box sx={{ display: 'flex', gap: '32px', width: '100%' }}>
                        <CustomPieChart data={buInvComplexity} title="Invoice complexity by BU" colours={coloursByStatusDonut} />
                        <Box sx={{ width: "65%" }}>
                            <TrendLineChartForBu dataset={trendsOfInvByBu} title="Trend of invoices by BU" bulist={buFilterData} xAxisLabel="Date"/>
                        </Box>
                    </Box>
                    <AgingTable rows={buExceptionList?.length ? buExceptionList : []} columns={columns} loading={isPending} title={"Business unit exception list"} />  
                </div>
            </div>
             }
        </div>
    )
}
